import routes from "@/app/routes";
import { ReleaseChangeHistoryButton } from "@/features/release";
import { ProjectSelector } from "@/ui/blocks/ProjectSelector";
import { ReleaseSelector } from "@/ui/blocks/ReleaseSelector";
import Breadcrumb from "@imminently/decisively-ui/Breadcrumb";
import React from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

interface RouteConfig {
  path: string;
  sidebar?: {
    name: string;
    translationKey?: string;
  };
  children?: RouteConfig[];
  exact?: boolean;
}

const findRouteByPath = (pathname: string, routeList: RouteConfig[]): RouteConfig | null => {
  for (const route of routeList) {
    // Check if this route matches
    if (route.path) {
      const routePath = Array.isArray(route.path) ? route.path[0] : route.path;
      const pathRegex = new RegExp(`^${routePath.replace(/:\w+/g, "[^/]+")}$`);

      if (pathRegex.test(pathname)) {
        return route;
      }
    }

    // Check children recursively
    if (route.children) {
      const childMatch = findRouteByPath(pathname, route.children);
      if (childMatch) return childMatch;
    }
  }
  return null;
};

const getPageTitle = (pathname: string, t: (key: string) => string): string => {
  // Try to find matching route
  const route = findRouteByPath(pathname, routes as RouteConfig[]);

  if (route?.sidebar) {
    return route.sidebar.translationKey ? t(route.sidebar.translationKey) : route.sidebar.name;
  }

  // Fallback: extract from pathname
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length > 0) {
    const lastSegment = segments[segments.length - 1];
    return lastSegment
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }

  return "Dashboard";
};

export const AppScope = () => {
  const location = useLocation();
  const { t } = useTranslation();

  const pageTitle = getPageTitle(location.pathname, t);
  const isMonitor = location.pathname === "/monitor" || location.pathname.startsWith("/monitor/");
  const showReleaseSelector = !isMonitor;

  return (
    <Breadcrumb className="flex items-center px-2 h-10 border-b border-border bg-background sticky top-0 z-10">
      <Breadcrumb.List className="w-full">
        <Breadcrumb.Item>
          <ProjectSelector />
        </Breadcrumb.Item>
        {showReleaseSelector ? (
          <>
            <Breadcrumb.Separator>/</Breadcrumb.Separator>
            <Breadcrumb.Item><ReleaseSelector /></Breadcrumb.Item>
          </>
        ) : null}
        <Breadcrumb.Separator>/</Breadcrumb.Separator>
        <Breadcrumb.Item className="pl-3">
          <Breadcrumb.Page>{pageTitle}</Breadcrumb.Page>
        </Breadcrumb.Item>
        {!isMonitor ? (
          <Breadcrumb.Item className="ml-auto">
            <ReleaseChangeHistoryButton />
          </Breadcrumb.Item>
        ) : null}
      </Breadcrumb.List>
    </Breadcrumb>
  );
};
